import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import {
  CBasketConfigurationKeys,
  CBASKET_NAMESPACE,
} from '@epicuro-next/data/baskets';
import {
  AccountsConfigurationKeys,
  AccountsConfigurations,
  ACCOUNTS_CONFIGURATIONS_DEFAULT_VALUES,
  ACCOUNTS_FEATURE_NAME,
  selectConfigurationValueFrom,
} from '@epicuro-next/platform/configuration';
import {
  resetAccountsSearchHistory,
  resetPaginationQuery,
  selectAccountEntities,
  selectAccountsQuerySearch,
  selectActiveAccountId,
} from '@epicuro-next/state/accounts-state';
import {
  CBasketConfigurations,
  CBasketService,
  loadDtoBasketSuccess,
  moveBasketSuccess,
  selectActiveBasketId,
  selectBasketId,
} from '@epicuro-next/state/basket-state';
import { selectRouteParams } from '@epicuro-next/state/router-store';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNonNullish, unique } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { tapResponse } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { Account, Address } from 'graphql-api/gql-api-interfaces';
import { Observable, of } from 'rxjs';
import {
  concatMap,
  filter,
  map,
  scan,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { getAddressUniqueId } from '../shared/get-address-unique-id/get-address-unique-id';
import { mapSelectedIdsToAccounts } from '../shared/map-selected-ids-to-accounts-selection/map-selected-ids-to-accounts-selection';

export interface MoveBasket {
  isMovingInProgress: boolean;
  accountIdsWithNoAvailableItemsErrors: string[];
}

export interface State {
  initialSelectedAccountIds: string[];
  initialSelectedAddressesIds: string[];

  selectedAccountIds: string[];
  selectedAddressesIds: string[];
  closeModal: boolean;

  moveBasket: MoveBasket;
}

export const initial: State = {
  initialSelectedAccountIds: [],
  initialSelectedAddressesIds: [],
  selectedAccountIds: [],
  selectedAddressesIds: [],
  closeModal: false,
  moveBasket: {
    isMovingInProgress: false,
    accountIdsWithNoAvailableItemsErrors: [],
  },
};

@Injectable()
export class AccountsSelectorComponentStore
  extends ComponentStore<State>
  implements OnDestroy
{
  private showDeselectAllEnabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        AccountsConfigurationKeys.ShowDeselectAll,
        AccountsConfigurations
      >(
        ACCOUNTS_FEATURE_NAME,
        AccountsConfigurationKeys.ShowDeselectAll,
        ACCOUNTS_CONFIGURATIONS_DEFAULT_VALUES[
          AccountsConfigurationKeys.ShowDeselectAll
        ],
      ),
    ),
  );

  /** determines if cross accounts selection is enabled */
  public isSingleSelection$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        AccountsConfigurationKeys.SingleSelectionKey,
        AccountsConfigurations
      >(
        ACCOUNTS_FEATURE_NAME,
        AccountsConfigurationKeys.SingleSelectionKey,
        ACCOUNTS_CONFIGURATIONS_DEFAULT_VALUES[
          AccountsConfigurationKeys.SingleSelectionKey
        ],
      ),
    ),
    map(Boolean),
  );

  /** determines if cross accounts selection is enabled */
  public shouldHighlightAddress$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        AccountsConfigurationKeys.HighlightFoundAddress,
        AccountsConfigurations
      >(
        ACCOUNTS_FEATURE_NAME,
        AccountsConfigurationKeys.HighlightFoundAddress,
        ACCOUNTS_CONFIGURATIONS_DEFAULT_VALUES[
          AccountsConfigurationKeys.HighlightFoundAddress
        ],
      ),
    ),
    map(Boolean),
  );

  public showSelectedAccountsEnabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        AccountsConfigurationKeys.ShowSelectedAccounts,
        AccountsConfigurations
      >(
        ACCOUNTS_FEATURE_NAME,
        AccountsConfigurationKeys.ShowSelectedAccounts,
        ACCOUNTS_CONFIGURATIONS_DEFAULT_VALUES[
          AccountsConfigurationKeys.ShowSelectedAccounts
        ],
      ),
    ),
  );

  public querySearch$ = this.store.pipe(select(selectAccountsQuerySearch));

  public searchCleared$ = this.querySearch$.pipe(
    map(Boolean),
    scan(
      ({ prev }, curr) => {
        return {
          prev: curr,
          isSearchCleared: !curr && prev,
        };
      },
      { prev: false, isSearchCleared: false },
    ),
    map(({ isSearchCleared }) => isSearchCleared),
    filter(Boolean),
  );
  public activeAccountId$ = this.store.pipe(select(selectActiveAccountId));

  public closeModal$ = this.select((state) => state.closeModal);

  public selectedAccountIds$ = this.select((state) => state.selectedAccountIds);
  public selectedAddressesIds$ = this.select(
    (state) => state.selectedAddressesIds,
  );
  public initialSelectedAccountIds$ = this.select(
    (state) => state.initialSelectedAccountIds,
  );
  public initialSelectedAddressesIds$ = this.select(
    (state) => state.initialSelectedAddressesIds,
  );
  public isConfirmDisabled$ = this.selectedAccountIds$.pipe(
    map((ids) => ids.length === 0),
  );
  public selectedShopsCount$ = this.selectedAddressesIds$.pipe(
    map((ids) => ids.length),
  );

  public selectedAccounts$ = this.select(
    this.selectedAccountIds$,
    this.store.pipe(select(selectAccountEntities)),
    (ids, entity) => ids.map((id) => entity[id]).filter(isNonNullish),
  );
  public addedAddressesCount$ = this.select(
    this.initialSelectedAddressesIds$,
    this.selectedAddressesIds$,
    (initialSelectedIds, selectedIds) => {
      return selectedIds.filter((id) => !initialSelectedIds.includes(id))
        .length;
    },
  );

  public removedAddressesCount$ = this.select(
    this.initialSelectedAddressesIds$,
    this.selectedAddressesIds$,
    (initialSelectedIds, selectedIds) => {
      return initialSelectedIds.filter((id) => !selectedIds.includes(id))
        .length;
    },
  );

  /**
   * returns first selected account due to user selection order if current is not still selected
   */
  public appliedActiveAccountId$ = this.select(
    this.activeAccountId$,
    this.selectedAccountIds$,
    (currentActiveAccountId, selectedAccountIds) => {
      return selectedAccountIds.includes(currentActiveAccountId as string)
        ? currentActiveAccountId
        : selectedAccountIds[0];
    },
  );

  public isActiveAccountChanged$ = this.select(
    this.activeAccountId$,
    this.appliedActiveAccountId$,
    (previous, current) => Boolean(previous && current && previous !== current),
  );

  public appliedBasketAccounts$ = this.select(
    this.store.pipe(select(selectAccountEntities)),
    this.selectedAccountIds$,
    this.selectedAddressesIds$,
    (accountEntity, selectedAccountIds, selectedAddressesIds) => {
      return mapSelectedIdsToAccounts(
        selectedAccountIds,
        selectedAddressesIds,
        accountEntity,
      );
    },
  );

  public appliedActiveAccount$ = this.select(
    this.appliedBasketAccounts$,
    this.appliedActiveAccountId$,
    (accounts, activeAccountId) => {
      return accounts.find(
        ({ accountId }) => accountId === activeAccountId,
      ) as Account;
    },
  );

  public isActiveBasketManipulated$ = this.select(
    this.store.pipe(select(selectRouteParams)),
    this.store.pipe(select(selectActiveBasketId)),
    (routeParams, activeBasketId) =>
      Boolean(activeBasketId && routeParams?.basketId === activeBasketId),
  );

  public isMoveBasketEnabled$ = this.store.pipe(
    select(
      selectConfigurationValueFrom<
        CBasketConfigurationKeys.MoveBasketEnabled,
        CBasketConfigurations
      >(CBASKET_NAMESPACE, CBasketConfigurationKeys.MoveBasketEnabled, false),
    ),
  );
  public isMoveBasketFlow$ = this.select(
    this.isMoveBasketEnabled$,
    this.selectedAccountIds$,
    this.initialSelectedAccountIds$,
    (moveBasketEnabled, selectedAccountIds, initialSelectedAccountIds) =>
      moveBasketEnabled &&
      selectedAccountIds.length === 1 &&
      !initialSelectedAccountIds.includes(selectedAccountIds[0]),
  );
  public isMovingBasketInProgress$ = this.select(
    (state) => state.moveBasket.isMovingInProgress,
  );
  public accountIdsWithNoAvailableItemsErrors$ = this.select(
    (state) => state.moveBasket.accountIdsWithNoAvailableItemsErrors,
  );
  public isSelectedAccountHasNoAvailableItemsError$ = this.select(
    this.accountIdsWithNoAvailableItemsErrors$,
    this.selectedAccountIds$,
    (failedAccountsIds, selectedAccountIds) =>
      failedAccountsIds.includes(selectedAccountIds[0]),
  );

  public isSelectedAccountsShown$ = this.select(
    this.showSelectedAccountsEnabled$,
    this.querySearch$,
    this.selectedAccountIds$,
    (isEnabled, search, selectedIds) =>
      Boolean(isEnabled && search && selectedIds.length),
  );

  public isDeselectAllShown$ = this.select(
    this.showDeselectAllEnabled$,
    this.selectedShopsCount$,
    (isEnabled, count) => isEnabled && count > 0,
  );

  constructor(
    private store: Store,
    private readonly basketService: CBasketService,
  ) {
    super(initial);
  }

  public readonly moveBasket = this.effect((void$: Observable<void>) => {
    return void$.pipe(
      concatMap(() =>
        of('').pipe(
          withLatestFrom(
            this.appliedActiveAccountId$,
            this.store.pipe(select(selectBasketId)),
          ),
        ),
      ),
      tap(() => this.setMovingBasket(true)),
      switchMap(([_, accountId, basketId]) => {
        return this.basketService
          .moveBasket(basketId as string, accountId as string)
          .pipe(
            tapResponse(
              (basket) => {
                this.setMovingBasket(false);
                this.setCloseModal();
                this.store.dispatch(
                  showSnackBarMessage({
                    message: $localize`:Account|Success to move basket message@@successToMoveBasketSnackbar:Basket moved successfully`,
                    messageType: MessageType.SUCCESS,
                  }),
                );
                this.store.dispatch(loadDtoBasketSuccess({ basket }));
                this.store.dispatch(
                  moveBasketSuccess({ basketId: basket.basketId }),
                );
              },
              (error: unknown) => {
                this.setMovingBasket(
                  false,
                  accountId as string,
                  (error as HttpErrorResponse)?.message,
                );
                this.store.dispatch(
                  showSnackBarMessage({
                    message: $localize`:Account|Failed to move basket message@@failedToMoveBasketSnackbar:Failed to move basket.`,
                    messageType: MessageType.DANGER,
                  }),
                );
              },
            ),
          );
      }),
    );
  });

  public ngOnDestroy() {
    this.selectedAccountIds$.pipe(take(1)).subscribe((skipIds) => {
      this.store.dispatch(resetAccountsSearchHistory({ skipIds }));
    });

    this.store.dispatch(resetPaginationQuery());
    super.ngOnDestroy();
  }

  public setInitialSelection(accounts: Account[]) {
    this.patchState((state) => {
      const selectedAccountIds = accounts.map(({ accountId }) => accountId);
      const selectedAddressesIds = accounts
        .flatMap(({ shipToAddresses }) => shipToAddresses)
        .filter(({ hasDeliveryItems }) => hasDeliveryItems)
        .flatMap((address) => getAddressUniqueId(address));

      return {
        ...state,
        initialSelectedAccountIds: selectedAccountIds,
        selectedAccountIds,
        initialSelectedAddressesIds: selectedAddressesIds,
        selectedAddressesIds,
      };
    });
  }

  public selectAccount(account: Account, isSingleSelection: boolean) {
    this.patchState((state) => {
      const selectedAccountIds = isSingleSelection
        ? [account.accountId]
        : [...state.selectedAccountIds, account.accountId].filter(unique);
      const selectedAddressesIds = isSingleSelection
        ? account.shipToAddresses.map(getAddressUniqueId)
        : [
            ...state.selectedAddressesIds,
            ...account.shipToAddresses.map(getAddressUniqueId),
          ].filter(unique);

      return {
        ...state,
        selectedAccountIds,
        selectedAddressesIds,
      };
    });
  }

  public deselectAccount(account: Account) {
    this.patchState((state) => ({
      ...state,
      selectedAccountIds: state.selectedAccountIds.filter(
        (accountId) => accountId !== account.accountId,
      ),
      selectedAddressesIds: state.selectedAddressesIds.filter(
        (addressId) =>
          !account.shipToAddresses.some(
            (accountAddress) =>
              getAddressUniqueId(accountAddress) === addressId,
          ),
      ),
    }));
  }

  public selectAddress(address: Address, isSingleSelection: boolean) {
    this.patchState((state) => {
      if (
        isSingleSelection &&
        !state.selectedAccountIds.includes(address.accountId)
      ) {
        return {
          ...state,
          selectedAccountIds: [address.accountId],
          selectedAddressesIds: [getAddressUniqueId(address)],
        };
      }
      return {
        ...state,
        selectedAddressesIds: [
          ...state.selectedAddressesIds,
          getAddressUniqueId(address),
        ],
        selectedAccountIds: [
          ...state.selectedAccountIds,
          address.accountId,
        ].filter(unique),
      };
    });
  }

  public deselectAll() {
    this.patchState(() => ({
      selectedAddressesIds: [],
      selectedAccountIds: [],
    }));
  }

  public deselectAddress(account: Account, address: Address) {
    this.patchState((state) => {
      const updatedSelectedAddresses = state.selectedAddressesIds.filter(
        (addressId) => addressId !== getAddressUniqueId(address),
      );
      const hasAccountOtherSelectedAddress = account.shipToAddresses
        .map(getAddressUniqueId)
        .some((accountAddressId) =>
          updatedSelectedAddresses.includes(accountAddressId),
        );

      return {
        ...state,
        selectedAddressesIds: updatedSelectedAddresses,
        selectedAccountIds: hasAccountOtherSelectedAddress
          ? state.selectedAccountIds
          : state.selectedAccountIds.filter(
              (selectedAccountId) => selectedAccountId !== account.accountId,
            ),
      };
    });
  }

  public setMovingBasket(
    isMovingInProgress: boolean,
    accountId?: string,
    errorMessage?: string,
  ) {
    const noBasketItemError = errorMessage?.includes(
      'No basket items are available',
    );
    this.patchState((state) => {
      const accountIdsWithNoAvailableItemsErrors =
        noBasketItemError && accountId
          ? [
              ...new Set(
                state.moveBasket.accountIdsWithNoAvailableItemsErrors.concat(
                  accountId,
                ),
              ),
            ]
          : state.moveBasket.accountIdsWithNoAvailableItemsErrors;

      return {
        moveBasket: {
          isMovingInProgress,
          accountIdsWithNoAvailableItemsErrors,
        },
      };
    });
  }

  public setCloseModal() {
    this.patchState(() => ({
      closeModal: true,
    }));
  }
}
